import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { adminLogin } from '../lib/api';
import { toast } from 'sonner';

const AdminLoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        // If already logged in as admin, redirect to dashboard
        const token = localStorage.getItem('hmc_admin_token');
        if (token) {
            navigate('/admin/dashboard');
        }
    }, [navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await adminLogin({ email, password });
            localStorage.setItem('hmc_admin_token', response.data.token);
            localStorage.setItem('hmc_admin', JSON.stringify(response.data.admin));
            toast.success('Welcome, Admin!');
            navigate('/admin/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Invalid credentials');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center py-12 px-4" data-testid="admin-login-page">
            <div className="max-w-md w-full">
                <div className="bg-white rounded-xl shadow-2xl p-8">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Shield className="w-8 h-8 text-amber-500" />
                        </div>
                        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Admin Portal</h1>
                        <p className="text-gray-600">Hotel Motel Club Management</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <Label htmlFor="email">Admin Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="admin@hotelmotelclub.com"
                                required
                                className="mt-1"
                                data-testid="admin-email-input"
                            />
                        </div>

                        <div>
                            <Label htmlFor="password">Password</Label>
                            <div className="relative mt-1">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter admin password"
                                    required
                                    className="pr-10"
                                    data-testid="admin-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            className="w-full bg-slate-900 hover:bg-slate-800"
                            disabled={loading}
                            data-testid="admin-login-btn"
                        >
                            {loading ? 'Signing in...' : 'Sign In to Admin'}
                        </Button>
                    </form>

                    <div className="mt-6 pt-6 border-t text-center">
                        <a href="/" className="text-sm text-gray-500 hover:text-amber-600">
                            ← Back to Main Site
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminLoginPage;
